import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheQueryInsights;

export type CacheQueryInsightsQueryData = {
  items: ApiResponse<typeof apiFn>['items'];
  summary: ApiResponse<typeof apiFn>['summary'];
  range: TimeRange;
  stepSeconds: number;
};

type CacheQueryInsightsQueryArgs<SELECT> = QueryOptions<
  CacheQueryInsightsQueryData,
  SELECT
> & {
  key: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  templateHash?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
};

const DBO_CACHE_QUERY_INSIGHTS_QUERY_KEY = 'dbo-cache-query-insights';

const getCacheQueryInsightsQueryKey = ({
  key,
  cacheGroupId,
  cacheConfigId,
  templateHash,
  stepSeconds,
}: {
  key?: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  templateHash?: string;
  stepSeconds?: number;
} = {}) =>
  [
    DBO_CACHE_QUERY_INSIGHTS_QUERY_KEY,
    key,
    cacheGroupId,
    cacheConfigId,
    templateHash,
    stepSeconds,
  ] as const;

const getCacheQueryInsightsQueryOptions = ({
  key,
  cacheGroupId,
  cacheConfigId,
  templateHash,
  stepSeconds = 60,
  getRange,
  enabled,
}: {
  key: string;
  cacheGroupId?: string;
  cacheConfigId?: string;
  templateHash?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getCacheQueryInsightsQueryKey({
      key,
      cacheGroupId,
      cacheConfigId,
      templateHash,
      stepSeconds,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        templateHash: templateHash!,
        cacheId: cacheConfigId!,
        groupId: cacheGroupId!,
        metricsRangeStart: range.from,
        metricsRangeEnd: range.to,
        stepSeconds: stepSeconds,
      });
      return { items: data.items, summary: data.summary, range, stepSeconds };
    },
    enabled: makeIsEnabled(
      !!cacheGroupId,
      !!cacheConfigId,
      !!templateHash,
      enabled
    ),
  });

export const useCacheQueryInsightsQuery = <
  SELECT = CacheQueryInsightsQueryData
>({
  key,
  cacheGroupId,
  cacheConfigId,
  templateHash,
  stepSeconds,
  getRange,
  enabled,
  select,
  refetchInterval,
}: CacheQueryInsightsQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheQueryInsightsQueryOptions({
      key,
      cacheGroupId,
      cacheConfigId,
      templateHash,
      stepSeconds,
      getRange,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
